import { ValidationError } from '@multiplier/lib-shared-errors';
import { Currency } from './Currency.js';
/**
 * Abstract base class for monetary values with currency.
 *
 * Uses F-bounded polymorphism (`Self`) so that instance methods return the
 * concrete subclass type rather than the base type. Each subclass implements
 * `construct` to produce instances of itself.
 *
 * @typeParam T - The underlying numeric value type (e.g. Decimal, LedgerBalance)
 * @typeParam Self - The concrete subclass type (F-bounded)
 */
export class MonetaryValueWithCurrency {
    value;
    currency;
    constructor(value, currency) {
        this.value = value;
        this.currency = currency;
    }
    /**
     * Shared factory logic for subclass static factories.
     * Validates null-for-non-zero invariant, resolves CurrencyCode → Currency, calls construct callback.
     */
    static createFromValue(value, currency, construct) {
        if (currency === null && !value.isZero()) {
            throw new ValidationError('Null currency can only be used for zero values');
        }
        const currencyObject = currency === null ? null : currency instanceof Currency ? currency : Currency.fromCode(currency);
        return construct(value, currencyObject);
    }
    /**
     * Adds two monetary amounts.
     * @param other - The monetary amount to add.
     * @returns The sum of the two amounts.
     */
    add(other) {
        if (this.currency === null && this.value.isZero()) {
            return other;
        }
        if (other.currency === null && other.value.isZero()) {
            return this;
        }
        if (this.currency !== other.currency) {
            throw new ValidationError('Cannot add monetary amounts with different currencies');
        }
        return this.construct(this.value.plus(other.value), this.currency);
    }
    /**
     * Subtracts one monetary amount from another.
     * @param other - The monetary amount to subtract.
     * @returns The difference.
     */
    subtract(other) {
        if (other.currency === null && other.value.isZero()) {
            return this;
        }
        if (this.currency === null && this.value.isZero()) {
            return other.construct(other.value.neg(), other.currency);
        }
        if (this.currency !== other.currency) {
            throw new ValidationError('Cannot subtract monetary amounts with different currencies');
        }
        return this.construct(this.value.minus(other.value), this.currency);
    }
    /**
     * @returns The underlying numeric value of this monetary amount.
     */
    getValue() {
        return this.value;
    }
    /**
     * @returns The currency object, or null if this is a currency-agnostic zero.
     */
    getCurrency() {
        return this.currency;
    }
    getCurrencyCode() {
        return this.currency?.code ?? null;
    }
    isCurrencylessZero() {
        return this.currency === null && this.value.isZero();
    }
    equals(other) {
        return this.value.equals(other.value) && this.currency === other.currency;
    }
    round(dp, rm) {
        return this.construct(this.value.toDecimalPlaces(dp, rm), this.currency);
    }
    roundToMinorUnits(rm) {
        if (this.currency === null) {
            return this;
        }
        return this.round(this.currency.minorUnits, rm);
    }
    negate() {
        return this.construct(this.value.neg(), this.currency);
    }
    neg() {
        return this.negate();
    }
    times(other) {
        return this.construct(this.value.times(other), this.currency);
    }
    /**
     * @returns A new instance in the same currency with a value of 0.
     */
    zero() {
        return this.construct(this.value.minus(this.value), this.currency);
    }
    toString() {
        return this.currency ? `${this.value.toString()} ${this.currency.code}` : this.value.toString();
    }
}
//# sourceMappingURL=MonetaryValueWithCurrency.js.map